import * as React from 'react'

import htmlReactParser, {
  Element,
  HTMLReactParserOptions,
} from 'html-react-parser'
import { computed, makeObservable, observable, runInAction } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { IRichText } from '~/client/graph'

import FileType from '../../../enums/FileType'
import IFilePreviewProperties from '../../../interfaces/IFilePreviewProperties'
import { SHOW_FULLSCREEN_PREVIEW } from '../../../stores/EventStore/eventConstants'
import EventsStore from '../../../stores/EventStore/Events.store'
import VideoPlayer from '../../VideoPlayer/VideoPlayer'
import WorkflowCardExpandableContainer from '../../WorkflowCard/ExpandableContainer'
import FileAttachment from '../../WorkflowCard/FileAttachment'
import { IMAGE_GALLERY_BLOT_NAME, VIDEO_BLOT_NAME } from './TextEditor'

import './RichEditorParsed.scss'

interface IProps {
  value: IRichText
  className?: string
  isContentExpanded?: boolean

  onMount?: () => void
  eventsStore?: EventsStore
}

@inject('eventsStore')
@observer
export default class RichEditorParsed extends React.Component<IProps> {
  @observable private value: IRichText = null

  public constructor(props: IProps) {
    super(props)

    this.value = props.value

    makeObservable(this)
  }

  public componentDidMount(): void {
    this.props.onMount?.()
  }

  public componentDidUpdate(prevProps: Readonly<IProps>) {
    if (prevProps.value !== this.props.value) {
      runInAction(() => (this.value = this.props.value))
    }
  }

  public render() {
    const { isContentExpanded, className } = this.props
    if (isContentExpanded) {
      return (
        <div
          className={classList({
            'rich-editor-parsed content-section px10': true,
            [className]: !!className,
          })}
          onClick={this.handleContentClick}
        >
          {this.announcementContent}
          {this.renderAttachments()}
        </div>
      )
    }

    return (
      <WorkflowCardExpandableContainer
        className={classList({
          'rich-editor-parsed content-section': true,
          [className]: !!className,
        })}
      >
        {this.announcementContent}
        {this.renderAttachments()}
      </WorkflowCardExpandableContainer>
    )
  }

  private handleContentClick = e => {
    const { eventsStore } = this.props
    if (e.target?.tagName?.toLowerCase() === 'img') {
      e.stopPropagation()
      const index = this.contentImages.findIndex(
        im => im.fileUrl === e.target.getAttribute('src'),
      )

      eventsStore.dispatch(SHOW_FULLSCREEN_PREVIEW, this.contentImages, index)
    }
  }

  @computed
  public get contentImages(): IFilePreviewProperties[] {
    const div = document.createElement('div')
    div.innerHTML = this.value.content
    const images = Array.from(div.getElementsByTagName('img')).map(im => ({
      fileType: FileType.Image,
      fileUrl: im.getAttribute('src'),
      fileName: im.getAttribute('alt'),
    }))

    div.remove()
    return images
  }

  @computed
  private get announcementContent(): JSX.Element | JSX.Element[] {
    const options: HTMLReactParserOptions = {
      replace: (element: Element) => {
        if (element.attribs?.class === IMAGE_GALLERY_BLOT_NAME) {
          delete element.attribs.contenteditable
          return element
        }

        if (element.attribs?.class === VIDEO_BLOT_NAME) {
          const src = element.attribs['data-video-src']
          return (
            <div data-video-src={src} className={VIDEO_BLOT_NAME}>
              <VideoPlayer src={src} />
            </div>
          )
        }
      },
    }

    return htmlReactParser(this.value.content, options)
  }

  private renderAttachments(): JSX.Element {
    const { attachments } = this.value || {}
    if (!attachments?.length) {
      return null
    }

    return (
      <div className="mt10">
        {attachments.map((attachment, idx) => (
          <FileAttachment
            key={idx}
            attachment={attachment}
            shouldShowPdfPreview={true}
            isPdfPreviewSmall={true}
          />
        ))}
      </div>
    )
  }
}
